import { Link } from "react-router-dom";
import { BookOpen, Users, MessageCircle, AlertTriangle, CheckCircle, FileText, Phone, Download } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const CLASSROOM_SIGNS = [
  "Ein Schüler wird regelmäßig ausgelacht oder verspottet",
  "Jemand sitzt immer alleine, wird nicht in Gruppen gewählt",
  "Gerüchte verbreiten sich schnell über eine bestimmte Person",
  "Ein Schüler fehlt häufig oder kommt oft zu spät",
  "Sachen verschwinden oder werden beschädigt",
  "Plötzlicher Leistungsabfall bei einem Schüler",
  "Schüler schauen verstohlen auf Handys und lachen",
  "Jemand wird 'zufällig' immer wieder angerempelt",
];

const INTERVENTION_STEPS = [
  {
    title: "Beobachten & Dokumentieren",
    desc: "Notieren Sie Vorfälle mit Datum, Uhrzeit, Beteiligten. Sammeln Sie Beweise.",
    icon: FileText,
    color: "#A0E8AF"
  },
  {
    title: "Einzelgespräche führen",
    desc: "Sprechen Sie zuerst einzeln mit dem Opfer, dann mit Tätern. Nie gemeinsam!",
    icon: MessageCircle,
    color: "#BDE0FE"
  },
  {
    title: "Eltern informieren",
    desc: "Kontaktieren Sie die Eltern beider Seiten. Bleiben Sie sachlich.",
    icon: Users,
    color: "#FFC4D6"
  },
  {
    title: "Konsequenzen festlegen",
    desc: "Klare Regeln und Konsequenzen bei Verstößen. Konsequent durchsetzen.",
    icon: AlertTriangle,
    color: "#F6E05E"
  },
];

const PREVENTION_TIPS = [
  "Klare Klassenregeln gegen Mobbing aufstellen",
  "Regelmäßig über Mobbing und Cybermobbing sprechen",
  "Klassengemeinschaft stärken durch Teamübungen",
  "Streitschlichter-Programm einführen",
  "Vertrauensvolle Atmosphäre schaffen",
  "Vorbildfunktion wahrnehmen - respektvoller Umgang",
];

export default function Lehrer() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Mobbing in der Schule – Leitfaden fuer Lehrkraefte" description="Mobbing im Klassenzimmer erkennen und bekampfen. Interventions- und Praeventionsstrategien fuer Lehrerinnen und Lehrer mit konkreten Handlungsschritten." path="/lehrer" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#BDE0FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-[#1A365D]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Informationen für Lehrkräfte
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Als Lehrkraft spielen Sie eine wichtige Rolle bei der Prävention und Intervention von Mobbing. 
              Hier finden Sie praktische Hilfen für den Schulalltag.
            </p>
          </div>

          {/* Warning Signs in Class */}
          <div className="bg-[#BDE0FE]/10 rounded-2xl p-6 md:p-8 mb-10">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-[#1A365D]" />
              <h2 className="text-xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>
                Mobbing im Klassenzimmer erkennen
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {CLASSROOM_SIGNS.map((sign, index) => (
                <div key={index} className="flex items-start gap-3 bg-white rounded-xl p-4">
                  <span className="text-[#1A365D]">•</span>
                  <span className="text-[#718096] text-sm">{sign}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Intervention Steps */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            Intervention: Schritt für Schritt
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {INTERVENTION_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-5 shadow-soft border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${step.color}30` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: step.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2D3748] mb-1">{step.title}</h3>
                      <p className="text-[#718096] text-sm">{step.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Prevention */}
          <div className="bg-[#A0E8AF]/10 rounded-2xl p-6 md:p-8 mb-10">
            <h2 className="text-xl font-bold text-[#1A4731] mb-6" style={{ fontFamily: 'Nunito' }}>
              🛡️ Prävention im Schulalltag
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {PREVENTION_TIPS.map((tip, index) => (
                <div key={index} className="flex items-start gap-3 bg-white/60 rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-[#A0E8AF] flex-shrink-0 mt-0.5" />
                  <span className="text-[#718096] text-sm">{tip}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Resources for Teachers */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            Materialien & Ressourcen
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <a 
              href="https://www.klicksafe.de/paedagogen"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
            >
              <Download className="w-8 h-8 text-[#BDE0FE] mb-3" />
              <h3 className="font-bold text-[#2D3748] mb-1">Unterrichtsmaterial</h3>
              <p className="text-[#718096] text-sm">Kostenlose Materialien von klicksafe</p>
            </a>
            
            <a 
              href="https://www.polizei-beratung.de/themen-und-tipps/gewalt/mobbing/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
            >
              <BookOpen className="w-8 h-8 text-[#A0E8AF] mb-3" />
              <h3 className="font-bold text-[#2D3748] mb-1">Polizeiliche Prävention</h3>
              <p className="text-[#718096] text-sm">Infos und Beratung der Polizei</p>
            </a>
          </div>

          {/* Important Note */}
          <div className="bg-[#F6E05E]/10 border border-[#F6E05E]/30 rounded-2xl p-6 mb-10">
            <h3 className="font-bold text-[#744210] mb-2">📌 Wichtiger Hinweis</h3>
            <p className="text-[#718096] text-sm">
              Bei schweren Fällen oder wenn Cybermobbing strafrechtlich relevant wird (Bedrohung, Nötigung, 
              Verbreitung intimer Bilder), sollten Sie die Schulleitung und ggf. die Polizei einschalten. 
              Dokumentieren Sie alle Vorfälle sorgfältig.
            </p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 text-center">
            <Users className="w-10 h-10 text-[#BDE0FE] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Gemeinsam gegen Mobbing
            </h3>
            <p className="text-[#718096] mb-6">
              Sie können uns auch als Lehrkraft kontaktieren, wenn Sie Unterstützung bei einem Fall brauchen 
              oder Materialien für Ihren Unterricht suchen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-6 py-3 rounded-full hover:bg-[#86D696] transition-all"
              >
                Fall melden
              </Link>
              <a 
                href="mailto:kontakt@mobbing-melden.de"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-6 py-3 rounded-full hover:bg-[#BDE0FE] transition-all"
              >
                Kontakt aufnehmen
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
